import { useEffect, useRef, useState } from 'react';
import type { CancelReason, ReceiptListItem } from '@matix/types';
import { useLang } from '../../lib/language';
import { formatNetValue } from './helpers';

/**
 * Void confirmation — the one thing standing between a mis-tap and a
 * cancelled sale.
 *
 * A void cannot be undone from the till, so this names the receipt being
 * cancelled (order number, total, customer) rather than asking an abstract
 * "are you sure?". Reading the number back is what catches the case this
 * dialog exists for: the wrong row was selected.
 *
 * THE REASON IS A PICKED ROW, NOT TYPED TEXT — the legacy till's
 * `ddlCancelReason` dropdown over `GL_OrderCancelationReasons`, with its
 * `#NoteVoid` textarea beneath it, which is what this screen's users already
 * know. The id goes to `Inv_Receipt_h.CancelReasonId`, so a void rung here
 * and a void rung on the legacy till land in head office's cancellation
 * reports as the same thing. A free-text box could never do that: "cold",
 * "COLD" and "was cold" are three reasons in a report and one in real life.
 *
 * Two departures from the legacy dialog, both deliberate:
 *
 *   - PICKING ONE IS OPTIONAL. The legacy page alerts "Please Select
 *     Cancellation Reason!" and refuses; this one lets the void through with
 *     nothing selected, on the user's instruction. So the empty option is
 *     always offered and is always what the dialog opens on — including when
 *     the tenant has exactly one reason, because otherwise the one case where
 *     "no reason" is unreachable would be the commonest tenant shape.
 *   - A tenant with NO reasons on file (two of them, as of the 2026-09-16
 *     fleet probe) gets the note alone rather than a dropdown with nothing in
 *     it. The legacy page shows an empty control there.
 *
 * Focus goes to the picker on open and Escape closes, matching the other
 * modals on this screen.
 */

export interface VoidReceiptDialogProps {
  /** The row being voided; null closes the dialog. */
  row: Pick<ReceiptListItem, 'id' | 'entry_no' | 'net_value' | 'customer_name'> | null;
  busy: boolean;
  /**
   * The tenant's reasons. Empty is a legitimate state — see above — and is
   * indistinguishable here from "still loading", which is why the screen
   * loads them before the dialog can be opened.
   */
  reasons: CancelReason[];
  onCancel: () => void;
  onConfirm: (cancelReasonId: number | null, note: string | null) => void;
}

/**
 * How one reason reads in the active language.
 *
 * Both ERP columns are NOT NULL and on most tenants hold the SAME string, so
 * this picks the session's side and falls back to the other rather than
 * rendering "بناء على طلب العميل — بناء على طلب العميل" in the dropdown.
 */
export function cancelReasonLabel(reason: CancelReason, arabic: boolean): string {
  const preferred = arabic ? reason.name_ar : reason.name;
  const fallback = arabic ? reason.name : reason.name_ar;
  return (preferred ?? '').trim() || (fallback ?? '').trim() || `#${reason.id}`;
}

export function VoidReceiptDialog({
  row,
  busy,
  reasons,
  onCancel,
  onConfirm,
}: VoidReceiptDialogProps) {
  const { t, lang } = useLang();
  const [reasonId, setReasonId] = useState<number | ''>('');
  const [note, setNote] = useState('');
  const selectRef = useRef<HTMLSelectElement | null>(null);
  const noteRef = useRef<HTMLTextAreaElement | null>(null);

  // Reset per receipt: a reason and a note chosen for one order must never
  // ride along to the next one the cashier opens this dialog for. It resets
  // to NO reason, which is a valid way to void — see above.
  useEffect(() => {
    setReasonId('');
    setNote('');
    if (row !== null) {
      const id = window.setTimeout(
        () => (reasons.length > 0 ? selectRef.current?.focus() : noteRef.current?.focus()),
        0,
      );
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [row?.id, reasons.length]);

  useEffect(() => {
    if (row === null) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [row, busy, onCancel]);

  if (row === null) return null;

  return (
    <div
      className="receipt-view-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div
        className="void-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={t({ en: 'Void receipt', ar: 'إلغاء الإيصال' })}
      >
        <header className="void-dialog-header">
          <h2>{t({ en: 'Void this receipt?', ar: 'إلغاء هذا الإيصال؟' })}</h2>
        </header>

        <div className="void-dialog-body">
          {/* The identity of what is about to be cancelled, large enough to
              be read at a glance — this is the whole point of the dialog. */}
          <div className="void-dialog-target">
            <span className="void-dialog-order">#{row.entry_no}</span>
            <span className="void-dialog-total">{formatNetValue(row.net_value)}</span>
          </div>
          {row.customer_name && (
            <div className="void-dialog-sub">{row.customer_name}</div>
          )}

          <p className="void-dialog-warning">
            {t({
              en: 'It will stop counting towards your shift, the Z-report and stock. This cannot be undone at the till.',
              ar: 'لن يُحتسب ضمن ورديتك ولا تقرير Z ولا المخزون. لا يمكن التراجع عن ذلك من نقطة البيع.',
            })}
          </p>

          {reasons.length > 0 && (
            <>
              <label className="void-dialog-label" htmlFor="void-reason">
                {t({ en: 'Cancelation reason (optional)', ar: 'سبب الإلغاء (اختياري)' })}
              </label>
              <select
                id="void-reason"
                ref={selectRef}
                className="receipts-input void-dialog-select"
                value={reasonId}
                disabled={busy}
                onChange={(e) => setReasonId(e.target.value === '' ? '' : Number(e.target.value))}
              >
                {/* Always offered, and always the one selected on open: voiding
                    without classifying is allowed, so it has to be reachable
                    without the cashier undoing a default. */}
                <option value="">{t({ en: 'No reason given', ar: 'بدون سبب' })}</option>
                {reasons.map((r) => (
                  <option key={r.id} value={r.id}>
                    {cancelReasonLabel(r, lang === 'ar')}
                  </option>
                ))}
              </select>
            </>
          )}

          <label className="void-dialog-label" htmlFor="void-note">
            {t({ en: 'Note (optional)', ar: 'ملاحظة (اختياري)' })}
          </label>
          <textarea
            id="void-note"
            ref={noteRef}
            className="receipts-input void-dialog-note"
            rows={3}
            maxLength={500}
            value={note}
            disabled={busy}
            onChange={(e) => setNote(e.target.value)}
            // On a tenant with no reason list there is nothing "above" for the
            // note to add to, so it asks the plain question instead.
            placeholder={
              reasons.length > 0
                ? t({
                    en: 'Anything the reason above does not say',
                    ar: 'أي تفاصيل لا يوضحها السبب أعلاه',
                  })
                : t({ en: 'Why is this being voided?', ar: 'ما سبب إلغاء هذا الإيصال؟' })
            }
          />
        </div>

        <footer className="void-dialog-actions">
          <button
            type="button"
            className="receipts-btn receipts-btn-secondary"
            disabled={busy}
            onClick={onCancel}
          >
            {t({ en: 'Keep receipt', ar: 'الاحتفاظ بالإيصال' })}
          </button>
          <button
            type="button"
            className="receipts-btn receipts-btn-danger"
            disabled={busy}
            onClick={() =>
              onConfirm(reasonId === '' ? null : reasonId, note.trim() || null)
            }
          >
            {busy
              ? t({ en: 'Voiding…', ar: 'جارٍ الإلغاء…' })
              : t({ en: 'Void receipt', ar: 'إلغاء الإيصال' })}
          </button>
        </footer>
      </div>
    </div>
  );
}
